import { useEffect, useRef, useState } from "react";

interface UsePullToRefreshOptions {
  /**
   * Callback disparado quando o usuário puxa além do `threshold` e solta.
   * Pode retornar uma Promise — enquanto ela não resolver, o indicador
   * permanece em estado "carregando".
   */
  onRefresh: () => void | Promise<void>;
  /** Distância (px) necessária para acionar o refresh ao soltar. */
  threshold?: number;
  /** Distância máxima (px) que o conteúdo pode ser arrastado. */
  maxPull?: number;
  /** Divisor de resistência — quanto maior, mais "duro" o arrasto. */
  resistance?: number;
  /** Altura (px) em que o indicador descansa enquanto carrega. */
  refreshingHeight?: number;
  /** Desabilita o gesto (ex.: modal aberto, onboarding bloqueado). */
  disabled?: boolean;
}

interface UsePullToRefreshResult {
  /** Deslocamento vertical atual do conteúdo (px). */
  pullDistance: number;
  /** True enquanto o refresh acionado ainda não terminou. */
  isRefreshing: boolean;
  /** True enquanto o dedo está na tela arrastando (sem transição CSS). */
  isPulling: boolean;
  /** Distância de acionamento, repassada para o indicador. */
  threshold: number;
}

const isAtTop = (): boolean =>
  (window.scrollY ||
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    0) <= 0;

/**
 * Pull-to-refresh para listagens cujo scroll acontece na `window`
 * (caso do NewStore: a TopBar é `position: fixed` e o feed rola a página).
 *
 * Detecta o arrasto para baixo apenas quando a página está no topo,
 * aplica uma curva de resistência e, ao soltar além do limiar, executa
 * `onRefresh`. A volta usa uma transição com leve overshoot (bounce).
 */
export function usePullToRefresh({
  onRefresh,
  threshold = 70,
  maxPull = 120,
  resistance = 2.5,
  refreshingHeight = 56,
  disabled = false,
}: UsePullToRefreshOptions): UsePullToRefreshResult {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);

  const startYRef = useRef<number | null>(null);
  const pullingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);

  // Mantém a referência mais recente do callback sem reanexar listeners.
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (disabled) {
      // Garante reset visual caso seja desabilitado no meio de um gesto.
      startYRef.current = null;
      pullingRef.current = false;
      pullDistanceRef.current = 0;
      setIsPulling(false);
      setPullDistance(0);
      return;
    }

    const resetPull = () => {
      pullingRef.current = false;
      pullDistanceRef.current = 0;
      startYRef.current = null;
      setIsPulling(false);
      setPullDistance(0);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (isRefreshingRef.current || !isAtTop()) {
        startYRef.current = null;
        return;
      }
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isRefreshingRef.current || startYRef.current === null) return;

      const delta = e.touches[0].clientY - startYRef.current;

      // Movimento para cima (ou voltou): cancela qualquer arrasto em curso.
      if (delta <= 0) {
        if (pullingRef.current) {
          pullingRef.current = false;
          pullDistanceRef.current = 0;
          setIsPulling(false);
          setPullDistance(0);
        }
        return;
      }

      // Se o usuário já não está mais no topo, aborta o gesto.
      if (!isAtTop()) {
        resetPull();
        return;
      }

      pullingRef.current = true;
      const distance = Math.min(maxPull, delta / resistance);
      pullDistanceRef.current = distance;
      setIsPulling(true);
      setPullDistance(distance);

      // Impede o overscroll/refresh nativo enquanto estamos no controle.
      if (e.cancelable) e.preventDefault();
    };

    const handleTouchEnd = () => {
      if (isRefreshingRef.current || !pullingRef.current) {
        startYRef.current = null;
        pullingRef.current = false;
        return;
      }

      const reached = pullDistanceRef.current >= threshold;
      pullingRef.current = false;
      startYRef.current = null;
      setIsPulling(false);

      if (!reached) {
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      // Acionou: mantém o indicador na altura de descanso até concluir.
      isRefreshingRef.current = true;
      pullDistanceRef.current = refreshingHeight;
      setIsRefreshing(true);
      setPullDistance(refreshingHeight);

      Promise.resolve()
        .then(() => onRefreshRef.current())
        .catch(() => {})
        .finally(() => {
          isRefreshingRef.current = false;
          pullDistanceRef.current = 0;
          setIsRefreshing(false);
          setPullDistance(0);
        });
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [disabled, threshold, maxPull, resistance, refreshingHeight]);

  return { pullDistance, isRefreshing, isPulling, threshold };
}
